import React, { useState, useMemo } from "react";
import { Modal, Form, Input, Button, Switch, message, Select } from "antd";
import OrderManager from "@/pages/Admin/OrdersManagement/component/OrderManager";
import CreateBulkOrderModal from "./modals/CreateBulkOrder";
import BulkOrderDetailsModal from "./modals/BulkOrderDetails";
import BulkFulfillmentModal from "@/pages/Admin/OrdersManagement/component/BulkFulfillmentModal";
import { useAddDriverMutation } from "@/services/api/driver/driverApi";
import { useGetLocationsQuery } from "@/services/api/location/locationApi";
import { useBulkOrderData } from "./hooks/useBulkOrderData";
import { formatCurrency, formatCustomerName, formatDate } from "@/utils/helper";
import OrderFilterDrawer from "../component/OrderFilterDrawer";
import { useArchiveOrderMutation } from "@/services/api/orders/ordersApi";
import useAuth from "@/hooks/useAuth";
import CustomModal from "@/components/UI/Modals/CustomModal";

const BulkOrders = () => {
  const {
    bulkOrdersData,
    summary,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    filters,
    handleFilterChange,
    handleClearFilters,
    hasActiveFilters,
    isLoading: bulkLoading,
    isFetching,
  } = useBulkOrderData();

  const [addDriver, { isLoading: addingDriver }] = useAddDriverMutation();
  const [archiveOrder, { isLoading: archiving }] = useArchiveOrderMutation(); // Add this
  const { data: locations = [], isLoading: locationLoading } =
    useGetLocationsQuery();
  const [driverForm] = Form.useForm();
  const user = useAuth(); // Add this

  // Modal states
  const [bulkOrderOpen, setBulkOrderOpen] = useState(false);
  const [openOrderDetails, setOpenOrderDetails] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [bulkFulfillmentOpen, setBulkFulfillmentOpen] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [showAddDriverModal, setShowAddDriverModal] = useState(false);
  const [newDriver, setNewDriver] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    license_number: "",
    status: true,
    location_id: null,
  });
  const handleAddDriver = async () => {
    if (
      newDriver?.first_name &&
      newDriver?.last_name &&
      newDriver?.phone &&
      newDriver?.email &&
      newDriver?.license_number &&
      newDriver?.location_id
    ) {
      try {
        await addDriver(newDriver).unwrap();
        message.success("Driver added successfully");
        setNewDriver({
          first_name: "",
          last_name: "",
          phone: "",
          email: "",
          license_number: "",
          status: true,
          location_id: 0,
        });
        setShowAddDriverModal(false);
      } catch (error) {
        message.error("Failed to add driver");
      }
    } else {
      message.error("Please fill in all required fields including location");
    }
  };
  // const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Location options for filter
  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: loc.id,
        label: `${loc.name} - ${loc.state?.name || ""}`,
      })),
    [locations]
  );

  // Map API data to table format
  const mappedBulkOrdersData = useMemo(() => {
    if (!bulkOrdersData || bulkOrdersData.length === 0) return [];

    return bulkOrdersData.map((order) => ({
      key: order.id.toString(),
      orderId: order.reference,
      customer: formatCustomerName(order.user),
      orderDate: formatDate(order.created_at),
      branch: order.location?.name || "Unknown Location",
      orderDueDate: formatDate(order.delivery_date),
      amount: formatCurrency(order.amount),
      items: order.items_count || 0,
      status: order.status?.name || "Unknown",
      originalData: order,
    }));
  }, [bulkOrdersData]);

  // Map API summary to metrics
  const orderMetrics = useMemo(() => {
    return [
      {
        title: "Total Orders",
        value: summary?.total_orders,
        change: "0",
        trend: "none",
      },
      {
        title: "Pending Orders",
        value: summary?.total_pending_orders,
        change: "0",
        trend: "none",
      },
      {
        title: "Completed Orders",
        value: summary?.total_completed_orders,
        change: "0",
        trend: "none",
      },
      {
        title: "Avg Processing Time",
        value: ` ${summary?.average_processing_time} Days `,
        change: "0",
        trend: "none",
      },
    ];
  }, [summary]);

  const openCreateModal = () => {
    setBulkOrderOpen(true);
  };

  const openDetailsModal = (orderData) => {
    setSelectedOrder(orderData);
    setOpenOrderDetails(true);
  };

  const closeOrderDetailsModal = () => {
    setOpenOrderDetails(false);
    setSelectedOrder(null);
  };

  const handleBulkFulfillment = (stage, orderKeys) => {
    // Extract original order IDs from the selected keys
    const orderIds = orderKeys
      .map((key) => {
        const order = mappedBulkOrdersData.find((o) => o.key === key);
        return order?.originalData?.id;
      })
      .filter(Boolean);

    setCurrentStage(stage);
    setSelectedOrderIds(orderIds);
    setBulkFulfillmentOpen(true);
  };

  const closeBulkFulfillmentModal = () => {
    setBulkFulfillmentOpen(false);
    setCurrentStage(null);
    setSelectedOrderIds([]);
  };

  // Add Bulk Archive Handler
  const handleBulkArchive = async (orderKeys) => {
    // Extract original order IDs from the selected keys
    const orderIds = orderKeys
      .map((key) => {
        const order = mappedBulkOrdersData.find((o) => o.key === key);
        return order?.originalData?.id;
      })
      .filter(Boolean);

    if (orderIds.length === 0) {
      message.warning("No valid orders selected for archiving");
      return;
    }

    try {
      await archiveOrder({
        order_ids: orderIds,
        archived_by: user?.id,
      }).unwrap();

      message.success(
        `${orderIds.length} order${
          orderIds.length > 1 ? "s" : ""
        } archived successfully!`
      );
    } catch (error) {
      message.error(
        error?.data?.message || "Failed to archive orders. Please try again."
      );
    }
  };

  // const handleAddDriver = async (values) => {
  //   try {
  //     await addDriver(values).unwrap();
  //     message.success("Driver added successfully!");
  //     setShowDriverDialog(false);
  //     driverForm.resetFields();
  //   } catch (error) {
  //     message.error("Failed to add driver. Please try again.");
  //   }
  // };

  return (
    <div>
      <OrderManager
        tableData={mappedBulkOrdersData}
        metricData={orderMetrics}
        buttonText="Create Bulk Order"
        tableTitle="All Bulk Orders"
        onCreate={openCreateModal}
        openDetailsModal={openDetailsModal}
        onBulkFulfillment={handleBulkFulfillment}
        onBulkArchive={handleBulkArchive} // Add this prop
        isLoading={bulkLoading || isFetching}
        archiving={archiving} // Add this prop
        paginationData={paginationInfo}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        search={search}
        handleSearch={handleSearch}
        searchMode="server"
        onFilterClick={() => setFilterDrawerOpen(true)}
        hasActiveFilters={hasActiveFilters}
      />

      <CreateBulkOrderModal
        open={bulkOrderOpen}
        onClose={() => setBulkOrderOpen(false)}
      />

      <BulkOrderDetailsModal
        open={openOrderDetails}
        onClose={closeOrderDetailsModal}
        orderData={selectedOrder}
      />

      <BulkFulfillmentModal
        open={bulkFulfillmentOpen}
        onClose={closeBulkFulfillmentModal}
        selectedOrderIds={selectedOrderIds}
        currentStage={currentStage}
        showAddDriverModal={() => setShowAddDriverModal(true)}
        // onShowDriverDialog={() => setShowDriverDialog(true)}
      />

      <OrderFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
        locationOptions={locationOptions}
        locationLoading={locationLoading}
      />

      {/* Add Driver Modal */}
      <CustomModal
        isOpen={showAddDriverModal}
        onClose={() => setShowAddDriverModal(false)}
        title="Add New Driver"
        subtitle="Enter driver information"
        primaryActionText="Add Driver"
        onPrimaryAction={handleAddDriver}
        secondaryActionText="Cancel"
        onSecondaryAction={() => setShowAddDriverModal(false)}
        width={600}
        rightOffset={0}
        loading={addingDriver}
      >
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
              <Input
                value={newDriver?.first_name}
                onChange={(e) =>
                  setNewDriver({ ...newDriver, first_name: e.target.value })
                }
                placeholder="Enter first name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
              <Input
                value={newDriver?.last_name}
                onChange={(e) =>
                  setNewDriver({ ...newDriver, last_name: e.target.value })
                }
                placeholder="Enter last name"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number *
              </label>
              <Input
                value={newDriver?.phone}
                onChange={(e) =>
                  setNewDriver({ ...newDriver, phone: e.target.value })
                }
                placeholder="+2348012345678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
              </label>
              <Input
                value={newDriver?.email}
                onChange={(e) =>
                  setNewDriver({ ...newDriver, email: e.target.value })
                }
                placeholder="johndoe@example.com"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                License Number *
              </label>
              <Input
                value={newDriver?.license_number}
                onChange={(e) =>
                  setNewDriver({ ...newDriver, license_number: e.target.value })
                }
                placeholder="LIC-123456"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location *
              </label>
              <Select
                value={newDriver?.location_id}
                onChange={(value) =>
                  setNewDriver({ ...newDriver, location_id: value })
                }
                className="w-full"
                placeholder="Select location"
                loading={locationLoading}
              >
                {locations?.map((location) => (
                  <Select.Option key={location.id} value={location.id}>
                    {location.name}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <Select
              value={newDriver?.status}
              onChange={(value) =>
                setNewDriver({ ...newDriver, status: value })
              }
              className="w-full"
            >
              <Select.Option value={true}>Active</Select.Option>
              <Select.Option value={false}>Inactive</Select.Option>
            </Select>
          </div>
        </div>
      </CustomModal>

      {/* Add Driver Modal
      <Modal
        open={showDriverDialog}
        onCancel={() => {
          setShowDriverDialog(false);
          driverForm.resetFields();
        }}
        title="Add New Driver"
        footer={null}
        destroyOnClose
      >
        <Form form={driverForm} layout="vertical" onFinish={handleAddDriver}>
          <Form.Item
            name="first_name"
            label="First Name"
            rules={[{ required: true, message: "Please enter first name" }]}
          >
            <Input placeholder="John" />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Last Name"
            rules={[{ required: true, message: "Please enter last name" }]}
          >
            <Input placeholder="Doe" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter phone number" },
              {
                pattern: /^\+?[0-9]{11,15}$/,
                message: "Enter valid phone number",
              },
            ]}
          >
            <Input placeholder="+2348012345678" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter email" },
              { type: "email", message: "Enter valid email" },
            ]}
          >
            <Input placeholder="johndoe@example.com" />
          </Form.Item>
          <Form.Item
            name="location_id"
            label="Location "
            rules={[{ required: true, message: "Please enter location ID" }]}
          >
            <Input type="number" placeholder="2" />
          </Form.Item>
          <Form.Item
            name="license_number"
            label="License Number"
            rules={[{ required: true, message: "Please enter license number" }]}
          >
            <Input placeholder="LIC-123456" />
          </Form.Item>
          <Form.Item name="status" valuePropName="checked" initialValue={true}>
            <div className="flex items-center gap-2">
              <Switch defaultChecked />
              <span>Active Status</span>
            </div>
          </Form.Item>
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => {
                setShowDriverDialog(false);
                driverForm.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={addingDriver}>
              Add Driver
            </Button>
          </div>
        </Form>
      </Modal> */}
    </div>
  );
};

export default BulkOrders;
